import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
} from 'react';
import pjson from '../../package.json';
import {
  ApolloProvider,
  ApolloLink,
  ApolloClient,
  HttpLink,
  from,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { handleErrors, notifyGraphQLError } from './errors/errorHandler';
import { cache } from './cache';
import { mutationTrackingLink } from './cache/saving';
import { apiEndpoints } from '../constants/endpoints';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { isProd } from '../utilities';
import Bugsnag from '@bugsnag/js';
import { useAuth0 } from '../providers/Auth0Provider';
import { setContext } from '@apollo/client/link/context';
import { CachePersistor } from 'apollo3-cache-persist';
import CustomSpinner from '../components/CustomSpinner';
import { getMainDefinition } from '@apollo/client/utilities';
import { paths } from '../constants/strings';
import localforage from 'localforage';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useFlags } from 'launchdarkly-react-client-sdk';

const defaultOptions = {
  watchQuery: {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  },
  query: {
    errorPolicy: 'all',
  },
  mutate: {
    errorPolicy: 'all',
  },
};

const uri = isProd ? apiEndpoints.prod : apiEndpoints.dev;

const httpLink = new HttpLink({
  uri,
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const extraData = {
    graphQLErrors,
    networkError,
    operation,
  };
  notifyGraphQLError(graphQLErrors?.[0] || networkError, extraData);
  handleErrors({ graphQLErrors, networkError, operation });
});

const namedLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${uri}?${operation.operationName}`,
  }));
  return forward(operation);
});

const ApolloContext = createContext();

export function useApolloContext() {
  return useContext(ApolloContext);
}

function ApiProvider({ children }) {
  const { getAccessTokenSilently, isLoading, logout } = useAuth0();
  const [client, setClient] = useState();
  const [isInitializing, setIsInitializing] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const online = useOnlineStatus();
  const { apolloPersisterCacheLimit } = useFlags();

  const MAX_PERSISTOR_SIZE = apolloPersisterCacheLimit * 1024 * 1024; // 8MB default (apollo-persister-cache-limit flag)

  const persistorRef = useRef(null);
  if (!persistorRef.current) {
    // LocalForageWrapper from apollo3-cache-persist has a bug: its setItem .catch
    // swallows the real DOMException and rejects with undefined, causing Bugsnag
    // to receive "[apollo-cache-persist] Error persisting cache undefined".
    // This wrapper fixes that by intercepting write errors before Persistor sees them.
    const safeStorage = {
      getItem: (key) => localforage.getItem(key),
      removeItem: (key) => localforage.removeItem(key),
      setItem: async (key, value) => {
        try {
          await localforage.setItem(key, value);
        } catch (err) {
          const isQuota =
            err?.name === 'QuotaExceededError' ||
            (err instanceof DOMException && err.code === 22) ||
            err == null;
          const error =
            err instanceof Error
              ? err
              : new Error(
                  isQuota
                    ? 'iOS IndexedDB quota exceeded'
                    : 'Apollo cache persist write failed',
                );
          if (isProd) {
            Bugsnag.notify(error, (event) => {
              event.severity = 'warning';
              event.addMetadata('apolloCache', {
                originalError: String(err),
                isQuotaError: isQuota,
              });
            });
          } else {
            console.warn('[Apollo cache] Write failed:', err);
          }
          const persistor = persistorRef.current;
          if (!persistor) return;
          persistor.pause();
          // Purge and schedule a resume so persistence doesn't stay off for the
          // rest of the session. If a purge happened within the last 6 hours,
          // stay paused — the cache will restore cleanly on the next app load
          // rather than thrashing write/fail/purge repeatedly.
          const lastPurge = localStorage.getItem('lastCachePurgeTime');
          const withinCooldown =
            lastPurge && Date.now() - Number(lastPurge) < 6 * 60 * 60 * 1000;
          if (!withinCooldown) {
            try {
              await persistor.purge();
              localStorage.setItem('lastCachePurgeTime', Date.now().toString());
              setTimeout(() => persistor.resume(), 3 * 60 * 1000);
            } catch (_) {
              // purge failed — stay paused, no resume scheduled
            }
          }
        }
      },
    };
    persistorRef.current = new CachePersistor({
      cache,
      storage: safeStorage,
      debug: !isProd,
      trigger: 'write',
      maxSize: MAX_PERSISTOR_SIZE,
    });
  }
  const newPersistor = persistorRef.current;
  const getDefaultOptions = (isOffline) =>
    isOffline
      ? {
          watchQuery: {
            errorPolicy: 'all',
            fetchPolicy: 'cache-only',
          },
          query: {
            errorPolicy: 'all',
            fetchPolicy: 'cache-only',
          },
          mutate: {
            errorPolicy: 'all',
            fetchPolicy: 'no-cache',
          },
        }
      : defaultOptions;

  async function getCachedToken() {
    const authStorageKey = Object.keys(localStorage).find((key) =>
      key.startsWith('@@auth0spajs@@'),
    );
    if (!authStorageKey) return null;
    try {
      const parsed = JSON.parse(localStorage.getItem(authStorageKey));
      if (parsed.access_token && parsed.expires_at * 1000 > Date.now()) {
        return parsed.access_token;
      }
    } catch (e) {}
    return null;
  }

  useEffect(() => {
    async function init() {
      setIsInitializing(true);
      await newPersistor.restore();
      const persistedSize = await newPersistor.getSize();
      if (persistedSize >= MAX_PERSISTOR_SIZE) {
        const lastPurge = localStorage.getItem('lastCachePurgeTime');
        const daysSinceLastPurge = lastPurge
          ? ((Date.now() - Number(lastPurge)) / (1000 * 60 * 60 * 24)).toFixed(
              1,
            )
          : 'never';
        const error = new Error(
          `Apollo cache at or over limit (${(
            persistedSize /
            1024 /
            1024
          ).toFixed(2)}MB) — persistence halted`,
        );
        if (isProd) {
          Bugsnag.notify(error, (event) => {
            event.addMetadata('apolloCache', {
              sizeMB: (persistedSize / 1024 / 1024).toFixed(2),
              daysSinceLastPurge,
              autoPurgedNow: online,
            });
          });
        } else {
          console.error(error, { daysSinceLastPurge, autoPurgedNow: online });
        }
        if (online) {
          await newPersistor.purge();
          localStorage.setItem('lastCachePurgeTime', Date.now().toString());
        }
      }

      const authLink = setContext(async (_, { headers }) => {
        if (!online) {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return {
            headers: {
              ...headers,
              user_id: user?.id,
              user_company_id: user?.company?.id,
              'x-test-user-id-e838st765d-816d-40c8-8347-12141d39479a': user?.id
                ? user.id.toString()
                : '',
              access_key: accessKey,
            },
          };
        }
        try {
          const cachedToken = await getCachedToken();
          const onlineToken = await getAccessTokenSilently();
          const token = online ? onlineToken : cachedToken;
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return {
            headers: {
              ...headers,
              authorization: token ? `Bearer ${token}` : '',
              user_id: user?.id,
              user_company_id: user?.company?.id,
              'x-test-user-id-e838st765d-816d-40c8-8347-12141d39479a': user?.id
                ? user.id.toString()
                : '',
              access_key: accessKey,
            },
          };
        } catch (e) {
          console.error({ e });
          return {
            headers: {
              ...headers,
              access_key: accessKey,
            },
          };
        }
      });

      const wsUri = isProd ? apiEndpoints.prodWs : apiEndpoints.devWs;

      const wsLink = new GraphQLWsLink(
        createClient({
          url: wsUri,
          connectionParams: async () => {
            if (!online) {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              return {
                Authorization: '',
                user_id: user?.id,
                user_company_id: user?.company?.id,
              };
            }
            const token = await getAccessTokenSilently();
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return {
              Authorization: token ? `Bearer ${token}` : '',
              user_id: user?.id,
              user_company_id: user?.company?.id,
            };
          },
        }),
      );
      const splitLink = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        wsLink,
        httpLink,
      );

      const client = new ApolloClient({
        cache,
        link: from([
          mutationTrackingLink,
          errorLink,
          namedLink,
          authLink.concat(splitLink),
        ]),
        name: pjson.name,
        version: pjson.version,
        defaultOptions: getDefaultOptions(!online),
      });

      setClient(client);
      setIsInitializing(false);
    }

    init().catch((error) => {
      console.error('Error initializing Apollo Client:', error);
      setIsInitializing(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, accessKey]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = async (event) => {
      if (event.data?.type !== 'APOLLO_CACHE_PRESSURE') return;

      console.log(
        '[Apollo] APOLLO_CACHE_PRESSURE received — ratioPercent:',
        event.data.ratioPercent,
      );

      // Rate limit: don't purge more than once every 6 hours
      const lastPurge = localStorage.getItem('lastCachePurgeTime');
      if (lastPurge && Date.now() - Number(lastPurge) < 6 * 60 * 60 * 1000) {
        const minsAgo = ((Date.now() - Number(lastPurge)) / 60000).toFixed(1);
        console.log(
          `[Apollo] Skipping purge — last purge was ${minsAgo} min ago (6hr cooldown active)`,
        );
        return;
      }

      const persistor = persistorRef.current;
      if (!persistor) return;

      // Size gate: only worth purging if Apollo is actually contributing meaningfully
      const size = await persistor.getSize().catch(() => 0);
      const sizeMB = (size / 1048576).toFixed(2);
      if (size < 4 * 1024 * 1024) {
        console.log(
          `[Apollo] Skipping purge — Apollo cache is only ${sizeMB}MB (threshold: 4MB)`,
        );
        return;
      }

      console.log(
        `[Apollo] Purging persisted Apollo cache (${sizeMB}MB) — in-memory cache untouched`,
      );
      await persistor.purge();
      localStorage.setItem('lastCachePurgeTime', Date.now().toString());
      console.log(
        '[Apollo] Purge complete — pausing persistence for 3 min to prevent immediate rebuild',
      );
      const { ratioPercent } = event.data;
      Bugsnag.notify(
        new Error('Apollo cache purged due to storage pressure'),
        (bugsnagEvent) => {
          bugsnagEvent.severity = 'warning';
          bugsnagEvent.addMetadata('apolloCache', {
            sizeMB,
            ratioPercent,
            trigger: 'SW_STORAGE_PRESSURE',
          });
        },
      );

      // Pause briefly so the cache doesn't immediately rebuild and undo the relief
      persistor.pause();
      setTimeout(() => {
        persistor.resume();
        console.log('[Apollo] Persistence resumed after 3 min pause');
      }, 3 * 60 * 1000);
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () =>
      navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  const signOut = async () => {
    if (newPersistor) {
      newPersistor.pause();
      client.resetStore().then(() => {
        newPersistor.purge();
      });
    }
    cache.reset();
    sessionStorage.clear();
    localStorage.clear();
    localforage.clear();
    logout({
      returnTo: `${window.location.origin}/${paths.login}`,
      federated: true,
    });
  };

  if (!client || isLoading || isInitializing) {
    return <CustomSpinner text={'Initializing App...'} />;
  }

  return (
    <ApolloContext.Provider
      value={{
        client,
        persistor: newPersistor,
        MAX_PERSISTOR_SIZE,
        signOut,
        accessKey,
        setAccessKey,
      }}
    >
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </ApolloContext.Provider>
  );
}

export default ApiProvider;
